/**
 * Maps the current host to a country context for the salunas country domains.
 * salunas.com (and localhost / anything else) → null country (all salons).
 * The same single build runs on every domain and inspects its own hostname.
 */
export interface CountryContext {
  code: string | null;   // 'BE' | 'PT' | 'FR' | 'NL' | null
  defaultLang: string;   // i18n code to default to when the user has no stored choice
  defaultCity: string;   // pre-filled city in the hero search ('' = none for this country)
}

interface CountryDomain {
  code: string;
  url: string;
  defaultLang: string;
  /** Country display name key for i18n (countrySwitcher.<key>). */
  i18nKey: string;
  flag: string;
  /**
   * City to pre-fill in the hero search bar on this domain. Only set where we
   * have a sensible default; '' (or omitted) means start empty so a Belgian
   * default city never leaks onto another country's domain.
   */
  defaultCity?: string;
}

// Static map — drives both detection and the switcher. Order = switcher order.
export const COUNTRY_DOMAINS: CountryDomain[] = [
  { code: 'BE', url: 'https://salunas.be', defaultLang: 'nl',    i18nKey: 'belgium',     flag: '🇧🇪', defaultCity: 'Brussels' },
  { code: 'PT', url: 'https://salunas.pt', defaultLang: 'pt-BR', i18nKey: 'portugal',    flag: '🇵🇹' },
  { code: 'FR', url: 'https://salunas.fr', defaultLang: 'fr',    i18nKey: 'france',      flag: '🇫🇷' },
  { code: 'NL', url: 'https://salunas.nl', defaultLang: 'nl',    i18nKey: 'netherlands', flag: '🇳🇱' },
];

const TLD_TO_CODE: Record<string, CountryDomain> = Object.fromEntries(
  COUNTRY_DOMAINS.map((d) => [d.code.toLowerCase(), d]),
);

export function detectCountryDomain(host: string = window.location.hostname): CountryContext {
  const bare = host.replace(/:\d+$/, '').replace(/^www\./, '').toLowerCase();
  const tld = bare.split('.').pop() ?? '';
  const match = TLD_TO_CODE[tld];
  // Only treat it as a country domain when the host is actually a salunas.<tld>.
  if (match && bare.startsWith('salunas.')) {
    return { code: match.code, defaultLang: match.defaultLang, defaultCity: match.defaultCity ?? '' };
  }
  // salunas.com / localhost / anything else: BE-centric main site keeps Brussels.
  return { code: null, defaultLang: 'en', defaultCity: 'Brussels' };
}
